import { EncoderDecoder } from '@iotize/common/converter/api';
import { isCodeError } from '@iotize/common/error';
import { TapError, TapResponseStatusError } from '@iotize/tap';
import { ResultCode } from '@iotize/tap/client/api';
import {
  VariableService,
  VariableType,
} from '@iotize/tap/service/impl/variable';

import { TapDataError } from '../../tap-data-error';
import { BLANK_CONVERTER } from '../../utility/blank-converter';
import { KeyTypeType } from '../../utility/editable-data-stream';
import { getConverterFromVariableType } from '../../utility/type-converter-factory';
import { AbstractVariable } from '../abstract-variable-stream';
import { TapVariableConfig } from '../tap-bundle/tap-bundle';

export function createTapVariableFromConfig<
  DataType,
  KeyType extends KeyTypeType,
>(
  variableService: VariableService,
  key: KeyType,
  value: TapVariableConfig<DataType>,
) {
  return new TapVariable<DataType, KeyType>(key, {
    variableService: variableService,
    config: {
      id: value.id,
      dataType: value.dataType,
      length: value.length,
    },
    converter:
      value.converter ||
      (value.dataType !== undefined
        ? getConverterFromVariableType(value.dataType, value.length || 1)
        : BLANK_CONVERTER),
  });
}

export namespace TapVariable {
  export interface Config {
    /**
     * Tap configured ID
     */
    id: number;

    dataType?: VariableType.Data;

    length?: number;
  }

  export interface InstanceOptions<DataType> {
    variableService: Pick<VariableService, 'getValue' | 'setValue'>;
    config: Config;
    converter?: EncoderDecoder<DataType, Uint8Array>;
    /**
     * Use this option if you want to extract only a subset of the data
     * For exemple if your Tap variable is configured to hold 10 bytes of data but you only want bytes 4, 5, 6, and 7 use subIndex option [4,5,6,7] (index starting from 0)
     */
    subIndex?: number[];
  }
}

export class TapVariable<
  DataType,
  KeyType extends KeyTypeType,
> extends AbstractVariable<DataType, KeyType> {
  get variableId() {
    return this.context.config.id;
  }

  get converter() {
    return this.context.converter || BLANK_CONVERTER;
  }

  get config(): TapVariable.Config {
    return this.context.config;
  }

  set converter(converter: EncoderDecoder<DataType, Uint8Array>) {
    this.context.converter = converter;
  }

  constructor(
    /**
     * Variable name identifier.
     */
    public readonly id: KeyType,
    public context: TapVariable.InstanceOptions<DataType>,
  ) {
    super();
  }

  async readRawInner(): Promise<Uint8Array> {
    try {
      let rawData = (
        await this.context.variableService.getValue(this.context.config.id)
      ).body();
      if (this.context.subIndex) {
        rawData = this.context.subIndex.reduce((acc, subIndex, index) => {
          acc[index] = rawData[subIndex];
          return acc;
        }, new Uint8Array(this.context.subIndex.length));
      }
      return rawData;
    } catch (err) {
      if (
        isCodeError<TapResponseStatusError>(
          TapError.Code.ResponseStatusError,
          err,
        )
      ) {
        switch (err.response.status) {
          case ResultCode.NOT_FOUND:
            throw TapDataError.variableNotConfigured(this.context.config, err);
        }
      }
      throw err;
    }
  }

  async writeRaw(value: Uint8Array): Promise<void> {
    try {
      if (this.context.subIndex) {
        const existingData = (
          await this.context.variableService.getValue(this.context.config.id)
        ).body();
        this.context.subIndex.forEach((subIndex, index) => {
          existingData[subIndex] = value[index];
        });
        value = existingData;
      }
      (
        await this.context.variableService.setValue(
          this.context.config.id,
          value,
        )
      ).body();
    } catch (err) {
      if (
        isCodeError<TapResponseStatusError>(
          TapError.Code.ResponseStatusError,
          err,
        )
      ) {
        switch (err.response.status) {
          case ResultCode.NOT_FOUND:
            throw TapDataError.variableNotConfigured(this.context.config, err);
        }
      }
      throw err;
    }
  }
}

// TODO add Sub variable data stream (IE read/write index )
